/**
 * Tool descriptions and prompts for AI models
 */

export const BROWSER_TOOL_DESCRIPTION = `Test and verify your implemented code in a live browser. This tool executes natural language test instructions and returns a video recording plus detailed logs to help you debug issues.

## When to Use
Use this AFTER coding is complete to verify your implementation:
- You've finished writing/modifying code and need to verify it works
- You need to test user interactions end-to-end (clicks, forms, navigation)
- You want to catch runtime errors, console warnings, or network failures
- You need visual confirmation that UI elements render and behave correctly

## What You Get Back
The tool returns debugging artifacts to help you identify and fix issues:
- **Video recording**: Watch exactly what happened in the browser session
- **Console logs**: All console.log, warnings, and errors with timestamps
- **Network logs**: Failed requests, 404s, API errors with screenshots
- **Error screenshots**: Visual snapshots captured when errors occur

## How to Write Good Tests
Be specific about the user journey you're testing:
❌ Bad: "Test the login feature"
✅ Good: "Navigate to /login, enter 'test@example.com' and 'password123', click the Login button, verify we reach the /dashboard page"

Include verification steps:
✅ "Click the Add Item button, verify the item appears in the list"
✅ "Submit the form, verify a success message is displayed"

## Iterating on Failures
1. Run the test and review the video + logs
2. Identify the specific issue (console error, failed request, wrong behavior)
3. Fix the code based on the evidence
4. Re-run the test to verify the fix
5. Repeat until test passes

## Requirements
- **URL**: Must be publicly accessible (use tunnels like ngrok, Cloudflare, or deploy to staging)
- **Timing**: Use this after implementation, not during coding
- **Complexity**: Set maxSteps higher (20-30) for multi-step user workflows`;


export const BROWSER_SYSTEM_PROMPT = `You are an AI agent designed to automate browser tasks to accomplish the <user_request>. Respond with a valid JSON object in the format: {"thinking": "Reason step-by-step about your current state, history, and the user request to decide your next goal and action. Analyze the browser state and screenshot to confirm the outcome of your last action.", "evaluation_previous_goal": "A concise, one-sentence evaluation of your last action's outcome (e.g., Success, Failure, or Uncertain).", "memory": "1-3 sentences summarizing key information and progress so far. This helps you track progress across multiple steps (e.g., items collected, pages visited).", "next_goal": "A clear, one-sentence description of your immediate next objective.", "action": [{"action_name": {"parameter": "value"}}]}`;
